import { useEffect } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router';
import { paths } from '../../../../common/constants/pathVariables.js';
import { CommonErrorType } from '../../../../common/enums.js';
import { postExternalAuthorizationRequest } from '../../../../common/fetch';
import { convertToCommonErrors } from '../../../../common/utils/errorUtils';
import { Spinner } from '../../../../public/site/siteUtils.js';
import { useAuth } from '../../AuthProvider.js';
import { toCommonErrors } from '../../authUtils';
import { useValidateUserAuthority } from './useValidateUserAuthority';

export const AuthenticationReturnPath = () => {
  const { identifyStrongly, setExternalAuthErrors } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { validateUserAuthority } = useValidateUserAuthority();

  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const codeQueryParam: string | null = queryParams.get('code');
    const stateQueryParam: string | null = queryParams.get('state');
    const errorQueryParam: string | null = queryParams.get('error');

    if (stateQueryParam?.startsWith('businessId') && stateQueryParam?.includes(':')) {
      if (codeQueryParam) {
        // Redirect to /rekisteroidy?redirectPath=/yhteystiedot
        // with /tunnistaudu/paluu?code=authCode&state=businessId:2698181-2:redirectPath:/yhteystiedot
        const queryState: string[] = stateQueryParam.split(':');
        void validateUserAuthority(codeQueryParam, queryState[1], queryState[3]);
      } else {
        // Redirect to /rekisteroidy with /tunnistaudu/paluu?state=businessId:2698181-2
        setExternalAuthErrors([{ message: '', type: CommonErrorType.BAD_REQUEST }]);
        navigate(paths.REGISTER_PAGE, { replace: true });
      }
    } else if (stateQueryParam?.startsWith('punchout')) {
      if (codeQueryParam) {
        // Redirect to /punchout/kassa with /tunnistaudu/paluu?code=idServiceCode&state=punchout
        identifyStrongly(codeQueryParam).catch(error => {
          toCommonErrors(error).then(commonErrors => setExternalAuthErrors([commonErrors[0]]));
        });
        navigate(paths.PUNCHOUT_CHECKOUT);
      } else {
        // Redirect to /punchout/kassa with /tunnistaudu/paluu?state=punchout
        setExternalAuthErrors([{ message: '', type: CommonErrorType.BAD_REQUEST }]);
        navigate(paths.PUNCHOUT_CHECKOUT, { replace: true });
      }
    } else if ((stateQueryParam && !codeQueryParam && !errorQueryParam) || (errorQueryParam && !stateQueryParam)) {
      // Redirect to /404 with /tunnistaudu/paluu?state=foobar or /tunnistaudu/paluu?error=foobar
      navigate(paths.NOT_FOUND_404);
    } else if (stateQueryParam) {
      if (errorQueryParam) {
        // Redirect to /tunnistaudu/vahvista-numeronsiirto/c49b856b-d1f8-7bda-ab7f-24e408104a83
        // with /tunnistaudu/paluu?state=c49b856b-d1f8-7bda-ab7f-24e408104a83&error=error
        // or /tunnistaudu/paluu?state=c49b856b-d1f8-7bda-ab7f-24e408104a83&error=error&error_description=cancelled
        navigate(generatePath(paths.AUTHORIZE_NUMBER_PORTING, { token: stateQueryParam }), {
          replace: true,
          state: { code: codeQueryParam, error: errorQueryParam },
        });
      }
      if (codeQueryParam) {
        // Redirect to /tunnistaudu/vahvistettu-numeronsiirto/c49b856b-d1f8-7bda-ab7f-24e408104a83
        // with /tunnistaudu/paluu?code=foobar&state=c49b856b-d1f8-7bda-ab7f-24e408104a83
        postExternalAuthorizationRequest(stateQueryParam, codeQueryParam)
          .then(() => {
            // Navigate to success page after successful authorization
            navigate(generatePath(paths.AUTHORIZED_NUMBER_PORTING, { token: stateQueryParam }), {
              replace: true,
              state: { success: true },
            });
          })
          .catch(error => {
            const errors = convertToCommonErrors(`ajax error ${error.status}`, error.status);
            navigate(generatePath(paths.AUTHORIZE_NUMBER_PORTING, { token: stateQueryParam }), {
              replace: true,
              state: { code: codeQueryParam, errors },
            });
          });
      }
    }
  }, [identifyStrongly, navigate, search, validateUserAuthority, setExternalAuthErrors]);

  return <Spinner />;
};
